import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { LuTag, LuX } from 'react-icons/lu'

import { GeofenceAlert } from '@/feature/observatory'
import { CheckboxInput } from '@/components/CheckboxInput'
import { FeatureFlag, useFlag, WhenFlag } from '@/feature/feature-flags'
import { ModalOpenVariant, TagsList, useTagsStore } from '@/feature/tags'
import { GeofenceAlertTagsPopout } from '@/feature/tags/components/GeofenceAlertTagsPopout/GeofenceAlertTagsPopout'

import { TooltipContainerProvider } from '@/lib/core/components/Tooltip'
import { RadioButtons } from '@/lib/core/components/RadioButtons'

import styles from './GeoFenceAlertDialog.module.css'
import { getFieldError, shouldShowError } from './GeoFenceCreateEdit'
import { InfoTooltip } from './InfoTooltip'

type GeoFenceAlertDialogProps = {
  alertRule?: GeofenceAlert
  open: boolean
  onClose: (index: number, onUpdate: boolean) => void
  onSaveAlertRule: (alertId: number | null) => void
  alertIndex: number
}

const DwellTimeFlagField = ({ alertIndex }: { alertIndex: number }) => {
  const { register, getFieldState, watch, setValue } = useFormContext()
  const enableDwellTime = watch(`alerts.${alertIndex}.enableDwellTime`)

  useEffect(() => {
    if (!enableDwellTime) {
      setValue(`alerts.${alertIndex}.dwellTime`, null)
    }
  }, [enableDwellTime])

  return (
    <>
      <CheckboxInput
        {...register(`alerts.${alertIndex}.enableDwellTime`)}
        error={shouldShowError(getFieldState(`alerts.${alertIndex}.enableDwellTime`))}
      >
        exceeds max dwell time
      </CheckboxInput>
      <label className={styles.dwellTimeInput}>
        <div className={styles.numberInput}>
          <input
            type="number"
            {...register(`alerts.${alertIndex}.dwellTime`, {
              disabled: !enableDwellTime,
            })}
            className={[
              styles.input,
              shouldShowError(getFieldState(`alerts.${alertIndex}.dwellTime`)) ? styles.error : '',
            ].join(' ')}
          />
          <div className={styles.radiusUnit}>min</div>
        </div>
        <div className={styles.errorLine}>
          {getFieldError(getFieldState(`alerts.${alertIndex}.dwellTime`))}
        </div>
      </label>
    </>
  )
}

const ContactsField = ({ alertIndex }: { alertIndex: number }) => {
  const { setValue, watch, getFieldState } = useFormContext()
  const enabledContactsWithPhoneNumber = useFlag<boolean>(
    'geofenceAlertContactsWithPhoneNumbers',
    false,
  )

  const tooltipMessage = `Add up to a total of 6 contacts - this can include a combination of email addresses (separated by commas) ${enabledContactsWithPhoneNumber ? 'and/or WhatsApp numbers in international format (e.g., +27821234567)' : ''} - to receive instant geofence alerts.`

  return (
    <label className={styles.textInput}>
      <div className={styles.inlineLabelWithInfoIcon}>
        Enter your contact details
        <InfoTooltip>{tooltipMessage}</InfoTooltip>
      </div>

      <input
        data-testid="alert-contacts-field"
        autoComplete="email tel"
        form="contact-search"
        name="search"
        type="search"
        value={watch(`alerts.${alertIndex}.contacts`)?.join(',') ?? ''}
        onChange={(e) => {
          setValue(
            `alerts.${alertIndex}.contacts`,
            e.target.value.split(',').map((v) => v.trim()),
            { shouldDirty: true, shouldTouch: true, shouldValidate: true },
          )
        }}
        className={[
          styles.input,
          shouldShowError(getFieldState(`alerts.${alertIndex}.contacts`)) ? styles.error : '',
        ].join(' ')}
      />
      <div className={styles.errorLine}>
        {getFieldError(getFieldState(`alerts.${alertIndex}.contacts`))}
      </div>
    </label>
  )
}

type AlertRuleType = 'allAssets' | 'assetsWithTags'

export const GeoFenceAlertDialog = ({
  alertRule,
  open,
  onClose,
  onSaveAlertRule,
  alertIndex,
}: GeoFenceAlertDialogProps) => {
  const alertDialog = useRef<HTMLDialogElement>(null)
  const [dialogElement, setDialogElement] = useState<HTMLDialogElement | null>(null)

  useEffect(() => {
    setDialogElement(alertDialog.current)
  }, [])
  const tagIconRef = useRef<HTMLDivElement>(null)
  const { openModal, toggleGeofenceTagsPopout, geofenceAlertTags, setGeofenceAlertTags } =
    useTagsStore()

  const [alertRuleType, setAlertRuleType] = useState<AlertRuleType | undefined>(
    alertRule?.tags?.length ? 'assetsWithTags' : 'allAssets',
  )

  const { register, getFieldState, setValue, resetField, watch, trigger } = useFormContext()

  useEffect(() => {
    if (open) {
      alertDialog.current?.showModal()
    } else {
      alertDialog.current?.close()
    }
  }, [open, alertDialog])

  useEffect(() => {
    setGeofenceAlertTags([])
    if (alertRuleType === 'assetsWithTags') {
      setGeofenceAlertTags(alertRule?.tags ?? [])
    }
  }, [alertRuleType])

  useEffect(() => {
    if (alertRuleType === 'assetsWithTags') {
      setValue(`alerts.${alertIndex}.tags`, geofenceAlertTags)
    }
  }, [alertRuleType, geofenceAlertTags])

  const handleTagIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (tagIconRef.current) {
      const rect = tagIconRef.current.getBoundingClientRect()
      const x = rect.left
      const y = rect.bottom

      toggleGeofenceTagsPopout({
        position: { x, y },
      })
    }
  }

  const onSave = async () => {
    const isValid = await trigger(`alerts.${alertIndex}`)

    if (!isValid) {
      return
    }

    onSaveAlertRule(alertRule?.id ?? null)
  }

  const onDiscard = () => {
    resetField(`alerts.${alertIndex}.title`)
    resetField(`alerts.${alertIndex}.tags`)
    resetField(`alerts.${alertIndex}.contacts`)
    resetField(`alerts.${alertIndex}.alertOnEnter`)
    resetField(`alerts.${alertIndex}.alertOnExit`)
    resetField(`alerts.${alertIndex}.alertOnDwell`)
    resetField(`alerts.${alertIndex}.dwellTime`)
    resetField(`alerts.${alertIndex}.enableDwellTime`)
    setGeofenceAlertTags(null)
    onClose(alertIndex, !!alertRule?.id)
  }

  return (
    <dialog ref={alertDialog} className={styles.alertDialog}>
      <TooltipContainerProvider container={dialogElement}>
        <header>
          <h3>Alert</h3>
          <button
            type="button"
            name="close"
            aria-label="close"
            onClick={onDiscard}
            className={styles.closeButton}
          >
            <LuX size={24} strokeWidth={1.5} />
          </button>
        </header>
        <section>
          <div className={styles.inputContainer}>
            <label className={styles.textInput}>
              <div className={styles.inlineLabelWithInfoIcon}>Alert Title</div>

              <input
                data-testid="alert-title-field"
                name="alert-title-field"
                value={watch(`alerts.${alertIndex}.title`) ?? ''}
                onChange={(e) => {
                  setValue(`alerts.${alertIndex}.title`, e.target.value, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  })
                }}
                className={[
                  styles.input,
                  shouldShowError(getFieldState(`alerts.${alertIndex}.title`)) ? styles.error : '',
                ].join(' ')}
              />
              <div className={styles.errorLine}>
                {getFieldError(getFieldState(`alerts.${alertIndex}.title`))}
              </div>
            </label>

            <div>Send an alert when</div>

            <FeatureFlag flag="assetTags">
              <WhenFlag>
                <div className={styles.radioButtonsContainer}>
                  <RadioButtons
                    name="send-alerts"
                    additionalStyles={{
                      minHeight: 40,
                    }}
                    currentValue={alertRuleType}
                    options={[
                      { value: 'allAssets', label: 'All Assets' },
                      { value: 'assetsWithTags', label: 'Assets with Tags' },
                    ]}
                    onChange={(value) => setAlertRuleType(value as AlertRuleType)}
                  />
                </div>
              </WhenFlag>
            </FeatureFlag>

            {alertRuleType === 'assetsWithTags' && (
              <div>
                <label className={styles.tagsLabel}>Vehicle Tags to Assign</label>
                <div className={styles.tagsSection}>
                  <div>
                    <div
                      ref={tagIconRef}
                      onClick={handleTagIconClick}
                      className={styles.tagsIcon}
                      data-testid="alert-rule-tags-icon"
                    >
                      <LuTag size={16} />
                    </div>
                  </div>
                  <div>
                    <TagsList
                      maxTagsRenderCount={5}
                      openModal={openModal}
                      modalVariant={ModalOpenVariant.FILTER}
                      tagIconRef={tagIconRef}
                      tagsList={geofenceAlertTags}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={styles.checkboxContainer}>
              <CheckboxInput
                {...register(`alerts.${alertIndex}.alertOnEnter`)}
                error={shouldShowError(getFieldState(`alerts.${alertIndex}.alertOnEnter`))}
              >
                enters geofence
              </CheckboxInput>
              <CheckboxInput
                {...register(`alerts.${alertIndex}.alertOnExit`)}
                error={shouldShowError(getFieldState(`alerts.${alertIndex}.alertOnExit`))}
              >
                exits geofence
              </CheckboxInput>
              <DwellTimeFlagField alertIndex={alertIndex} />
            </div>
            <ContactsField alertIndex={alertIndex} />
          </div>
        </section>
        <footer>
          <div className={styles.footerContainer}>
            <button
              type="button"
              name="cancel"
              aria-label="cancel"
              onClick={onDiscard}
              className={styles.cancelButton}
            >
              Discard
            </button>
            <button
              type="button"
              name="discard"
              aria-label="discard"
              onClick={onSave}
              className={styles.discardButton}
            >
              Save
            </button>
          </div>
        </footer>

        <GeofenceAlertTagsPopout />
      </TooltipContainerProvider>
    </dialog>
  )
}
